// src/shell/AssistantWidget.jsx — floating "brian" assistant launcher (Figma 1.1).
// Bottom-right FAB that toggles a chat panel. UI stub only: no backend is wired yet,
// so sends get a canned "coming soon" reply. Swap `replyTo()` for a real call later.
import React, { useState, useRef, useEffect } from 'react';
import { X, Send } from 'lucide-react';
import { COLORS, FONT_SANS, RADII } from '../constants/theme.js';

function BrianGlyph({ size = 26, color = COLORS.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill={color} xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path d="M38.2966 8.77426L32.5513 14.5408C30.7496 16.3492 28.1694 16.8634 25.8155 15.8849C23.4616 14.9065 22 12.711 22 10.1541V1.99927H26.4146V10.1541C26.4146 11.2526 27.2486 11.6854 27.5048 11.7911C27.7609 11.8968 28.6547 12.1839 29.4289 11.4068L35.1742 5.64031L38.2951 8.77283L38.2966 8.77426Z" />
      <path d="M14.6153 21.9971H6.49048V17.5662H14.6153C15.7097 17.5662 16.1409 16.7291 16.2462 16.472C16.3515 16.2149 16.6376 15.3178 15.8634 14.5408L10.1181 8.77425L13.2391 5.64172L18.9844 11.4082C20.7861 13.2166 21.2984 15.8063 20.3236 18.169C19.3487 20.5316 17.1613 21.9985 14.6139 21.9985L14.6153 21.9971Z" />
      <path d="M26.4146 37.564H22V29.4092C22 28.3108 21.166 27.8779 20.9098 27.7722C20.6537 27.6665 19.7599 27.3794 18.9857 28.1565L5.19252 42.0007L2.07581 38.8625L15.8633 25.024C17.6651 23.2156 20.2452 22.6999 22.5991 23.6798C24.953 24.6583 26.4146 26.8538 26.4146 29.4106V37.5655V37.564Z" />
      <path d="M35.1741 33.923L29.4288 28.1565C27.6271 26.3481 27.1148 23.7584 28.0896 21.3958C29.0645 19.0331 31.2519 17.5662 33.7993 17.5662H41.9241V21.9971H33.7993C32.7049 21.9971 32.2737 22.8342 32.1684 23.0913C32.0631 23.3484 31.777 24.2454 32.5512 25.0225L38.2965 30.789L35.1755 33.9216L35.1741 33.923Z" />
    </svg>
  );
}

const GREETING = { role: 'assistant', text: 'Ciao! Sono brian, il tuo assistente. Come posso aiutarti a creare contenuti oggi?' };

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const listRef = useRef(null);

  // Keep the message list scrolled to the latest bubble.
  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages((m) => [
      ...m,
      { role: 'user', text },
      { role: 'assistant', text: 'L’assistente conversazionale sarà presto disponibile. Nel frattempo, usa il Content Generator per creare i tuoi contenuti.' },
    ]);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div style={{ position: 'fixed', right: '24px', bottom: '24px', zIndex: 80, fontFamily: FONT_SANS }}>
      {open && (
        <div
          role="dialog"
          aria-label="Assistente brian"
          style={{
            position: 'absolute', bottom: '72px', right: 0,
            width: '360px', maxWidth: 'calc(100vw - 48px)', height: '480px', maxHeight: 'calc(100vh - 140px)',
            display: 'flex', flexDirection: 'column',
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: RADII.control,
            boxShadow: '0 16px 48px rgba(35,0,62,0.20)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: RADII.pill,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: COLORS.accentTint,
            }}>
              <BrianGlyph size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.ink }}>Assistente brian</div>
              <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>Sempre a portata di mano</div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Chiudi assistente"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '30px', height: '30px', border: 'none', background: 'transparent',
                color: COLORS.textSecondary, cursor: 'pointer', borderRadius: RADII.control,
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {messages.map((m, i) => (
              <div
                key={i}
                style={{
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  padding: '10px 12px',
                  borderRadius: RADII.card,
                  fontSize: '13px', lineHeight: 1.5,
                  background: m.role === 'user' ? COLORS.accent : COLORS.field,
                  color: m.role === 'user' ? COLORS.surface : COLORS.ink,
                  borderBottomRightRadius: m.role === 'user' ? '4px' : '14px',
                  borderBottomLeftRadius: m.role === 'user' ? '14px' : '4px',
                }}
              >
                {m.text}
              </div>
            ))}
          </div>

          {/* Composer */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', padding: '12px', borderTop: `1px solid ${COLORS.border}` }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Scrivi un messaggio…"
              style={{
                flex: 1, resize: 'none', maxHeight: '96px',
                padding: '10px 12px', borderRadius: RADII.control,
                border: `1px solid ${COLORS.border}`, background: COLORS.field,
                color: COLORS.ink, fontSize: '13px', fontFamily: FONT_SANS, lineHeight: 1.4,
                outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim()}
              aria-label="Invia"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '40px', height: '40px', flexShrink: 0,
                border: 'none', borderRadius: RADII.control,
                background: input.trim() ? COLORS.accent : COLORS.border,
                color: COLORS.surface, cursor: input.trim() ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Launcher FAB */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? 'Chiudi assistente' : 'Apri assistente brian'}
        aria-expanded={open}
        style={{
          width: '56px', height: '56px', borderRadius: RADII.pill,
          border: `1px solid ${COLORS.border}`, background: COLORS.surface,
          boxShadow: '0 8px 24px rgba(35,0,62,0.18)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginLeft: 'auto',
        }}
      >
        {open ? <X size={24} color={COLORS.accent} /> : <BrianGlyph size={28} />}
      </button>
    </div>
  );
}
